import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Search, Loader2, CheckCircle2, Download, RotateCcw, ExternalLink, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCities } from "@/context/CitiesContext";
import { useCategories } from "@/context/CategoriesContext";
import { toast } from "sonner";

const inputCls = "w-full bg-black/40 border border-white/10 px-3.5 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-ec-volt transition-colors";

function fmt(iso) {
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

const STEPS = ["Discover", "Select", "Harvest", "Review"];

export default function Discover() {
  const { cities } = useCities();
  const { sportLabel } = useCategories();
  const [city, setCity] = useState("");
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickedProviders, setPickedProviders] = useState({});
  const [pickedEvents, setPickedEvents] = useState({});
  const poll = useRef(null);

  useEffect(() => {
    if (cities.length && !city) setCity(cities[0].name);
  }, [cities]); // eslint-disable-line

  const stopPoll = () => { if (poll.current) { clearInterval(poll.current); poll.current = null; } };
  useEffect(() => stopPoll, []);

  const pollRun = (id) => {
    stopPoll();
    poll.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/discovery/${id}`);
        setRun(data);
        if (["providers_ready", "review_ready", "failed"].includes(data.status)) {
          stopPoll();
          if (data.status === "providers_ready") {
            const init = {}; data.providers.forEach((p) => { init[p.id] = true; });
            setPickedProviders(init);
          }
          if (data.status === "review_ready") {
            const init = {}; data.events.forEach((e) => { init[e.id] = true; });
            setPickedEvents(init);
          }
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const startDiscovery = async () => {
    if (!city.trim()) { toast.error("Enter a city"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/discovery", { city });
      setRun(data); pollRun(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const harvest = async () => {
    const ids = Object.keys(pickedProviders).filter((k) => pickedProviders[k]);
    if (!ids.length) { toast.error("Select at least one provider"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/discovery/${run.id}/harvest`, { provider_ids: ids });
      setRun({ ...run, status: "harvesting" }); pollRun(run.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const importEvents = async () => {
    const ids = Object.keys(pickedEvents).filter((k) => pickedEvents[k]);
    if (!ids.length) { toast.error("Select at least one event"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/discovery/${run.id}/import`, { event_ids: ids });
      toast.success(`${data.imported} events imported to the approval queue`);
      reset();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reset = () => { stopPoll(); setRun(null); setPickedProviders({}); setPickedEvents({}); };

  const status = run?.status;
  const stepIndex = !run ? 0 : status === "discovering" ? 0 : status === "providers_ready" ? 1 : status === "harvesting" ? 2 : 3;

  return (
    <div className="space-y-8" data-testid="discover">
      <div>
        <h2 className="font-heading text-3xl tracking-wide flex items-center gap-2"><Sparkles size={20} className="text-ec-volt" /> AI Event Discovery</h2>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">Point the agent at a city. It searches the web for event providers, suggests a category for each, then — for the ones you pick — harvests their upcoming events for you to review before anything is imported.</p>
      </div>

      {/* stepper */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="discover-steps">
        {STEPS.map((s, i) => (
          <div key={s} className={`inline-flex items-center gap-2 overline px-3 py-1.5 border ${i === stepIndex ? "border-ec-volt text-ec-volt" : i < stepIndex ? "border-white/15 text-zinc-400" : "border-white/10 text-zinc-600"}`}>
            <span className="w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] border border-current">{i + 1}</span>{s}
          </div>
        ))}
        {run && <button onClick={reset} data-testid="discover-reset" className="ml-auto inline-flex items-center gap-1.5 overline text-zinc-400 hover:text-ec-red transition-colors"><RotateCcw size={13} /> Start over</button>}
      </div>

      {/* STEP 1: city input */}
      {!run && (
        <div className="bg-ec-surface border border-white/10 p-6 max-w-xl" data-testid="discover-start">
          <label className="block mb-4"><span className="overline text-zinc-500 mb-2 block">City to search</span>
            <input data-testid="discover-city" list="ec-cities" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} placeholder="e.g. Worcester, UK" />
            <datalist id="ec-cities">{cities.map((c) => <option key={c.slug} value={c.name} />)}</datalist>
          </label>
          <button onClick={startDiscovery} disabled={busy} data-testid="discover-start-btn" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-6 py-3 hover:-translate-y-0.5 transition-all disabled:opacity-50">
            <Search size={16} /> {busy ? "Starting…" : "Discover Providers"}
          </button>
        </div>
      )}

      {status === "discovering" && <Working label={`Searching the web for event providers in ${run.city}…`} />}
      {status === "harvesting" && <Working label="Harvesting events from the selected providers… this can take a minute." sub={run.providers?.filter((p) => p.selected).map((p) => `${p.name}: ${p.harvest_status === "ok" ? `${p.events_found} found` : p.harvest_status === "failed" ? "failed" : "…"}`).join("  ·  ")} />}

      {status === "failed" && (
        <div className="bg-ec-surface border border-ec-red/40 p-6" data-testid="discover-error">
          <p className="text-ec-red flex items-center gap-2"><AlertTriangle size={16} /> {run.error || "Something went wrong."}</p>
          <button onClick={reset} className="mt-4 overline border border-white/15 px-4 py-2 hover:border-ec-volt hover:text-ec-volt">Try again</button>
        </div>
      )}

      {/* STEP 2: provider selection */}
      {status === "providers_ready" && (
        <div className="space-y-4" data-testid="discover-providers">
          <p className="text-sm text-zinc-400">Found <span className="text-ec-volt">{run.providers.length}</span> providers in {run.city}. Tick the ones to harvest.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {run.providers.map((p) => (
              <label key={p.id} data-testid={`provider-${p.id}`} className={`block border p-4 cursor-pointer transition-colors ${pickedProviders[p.id] ? "border-ec-volt bg-ec-volt/5" : "border-white/10 bg-black/30"}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" data-testid={`provider-check-${p.id}`} checked={!!pickedProviders[p.id]} onChange={(e) => setPickedProviders({ ...pickedProviders, [p.id]: e.target.checked })} className="accent-ec-volt w-4 h-4 mt-1" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-heading text-xl tracking-wide leading-none">{p.name}</h4>
                      <span className="overline bg-white/10 px-2 py-0.5 text-ec-volt">{sportLabel(p.category)}</span>
                    </div>
                    <a href={p.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-zinc-500 hover:text-ec-volt underline break-all inline-flex items-center gap-1">{p.url} <ExternalLink size={11} /></a>
                    {p.reason && <p className="text-xs text-zinc-500 mt-1">{p.reason}</p>}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <button onClick={harvest} disabled={busy} data-testid="discover-harvest-btn" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-6 py-3 hover:-translate-y-0.5 transition-all disabled:opacity-50">
            <Sparkles size={16} /> Harvest events from {Object.values(pickedProviders).filter(Boolean).length} providers
          </button>
        </div>
      )}

      {/* STEP 4: review + import */}
      {status === "review_ready" && (
        <div className="space-y-4" data-testid="discover-review">
          {run.events.length === 0 ? (
            <div className="border border-white/10 bg-ec-surface p-10 text-center">
              <p className="font-heading text-2xl text-zinc-400">No events found</p>
              <p className="text-zinc-500 mt-2 text-sm">The selected providers didn't expose readable events. Try different providers or add their ICS feed manually.</p>
              <button onClick={reset} className="mt-4 overline border border-white/15 px-4 py-2 hover:border-ec-volt hover:text-ec-volt">Start over</button>
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-400">Harvested <span className="text-ec-volt">{run.events.length}</span> events. Review and import the ones you want — they'll land in the approval queue as pending.</p>
              <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm" data-testid="discover-events-table">
                  <thead className="bg-black/50"><tr className="text-left overline text-zinc-500">
                    <th className="p-2.5"></th><th className="p-2.5">Title</th><th className="p-2.5">Provider</th><th className="p-2.5">Category</th><th className="p-2.5">When</th><th className="p-2.5">Link</th>
                  </tr></thead>
                  <tbody>
                    {run.events.map((e) => (
                      <tr key={e.id} data-testid={`discover-event-${e.id}`} className={pickedEvents[e.id] ? "bg-ec-volt/5" : ""}>
                        <td className="p-2.5"><input type="checkbox" data-testid={`event-check-${e.id}`} checked={!!pickedEvents[e.id]} onChange={(ev) => setPickedEvents({ ...pickedEvents, [e.id]: ev.target.checked })} className="accent-ec-volt w-4 h-4" /></td>
                        <td className="p-2.5 font-semibold">{e.title}</td>
                        <td className="p-2.5 text-zinc-400">{e.provider_name}</td>
                        <td className="p-2.5"><span className="overline text-ec-volt">{sportLabel(e.category)}</span></td>
                        <td className="p-2.5 text-zinc-400 whitespace-nowrap">{fmt(e.start_at)}</td>
                        <td className="p-2.5">{e.ticket_url && <a href={e.ticket_url} target="_blank" rel="noreferrer" className="text-ec-volt"><ExternalLink size={14} /></a>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={importEvents} disabled={busy} data-testid="discover-import-btn" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-7 py-3.5 hover:-translate-y-0.5 transition-all disabled:opacity-50">
                <Download size={16} /> Import {Object.values(pickedEvents).filter(Boolean).length} to approval queue
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Working({ label, sub }) {
  return (
    <div className="bg-ec-surface border border-white/10 p-8 flex flex-col items-center text-center" data-testid="discover-working">
      <Loader2 size={28} className="text-ec-volt animate-spin" />
      <p className="font-heading text-2xl tracking-wide mt-4">{label}</p>
      {sub && <p className="text-xs text-zinc-500 mt-2 max-w-xl">{sub}</p>}
    </div>
  );
}
